import { useEffect, useState } from "react";
import { addDays, format, parseISO, startOfToday } from "date-fns";
import { CalendarDays, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const VisitScheduler = ({ appId, date, slot, onChange }) => {
  const [avail, setAvail] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/visits/availability").then((r) => setAvail(r.data)).catch(() => {});
  }, []);

  const slots = avail?.slots || [];
  const max = avail?.max_per_slot ?? 2;
  const today = startOfToday();
  const horizon = addDays(today, avail?.horizon_days ?? 30);
  const bookedFor = (d, s) => (avail?.booked?.[d]?.[s] || []).filter((id) => id !== appId).length;
  const dayFull = (d) => slots.length > 0 && slots.every((s) => bookedFor(format(d, "yyyy-MM-dd"), s) >= max);
  const selected = date ? parseISO(date) : undefined;

  return (
    <div className="space-y-3" data-testid="visit-scheduler">
      <div className="flex gap-2">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="flex-1 justify-start h-11 font-normal" data-testid="vip-visit-date-picker">
              <CalendarDays className="h-4 w-4 mr-2 text-[#FF6B00]" />
              {selected ? format(selected, "EEE, dd MMM yyyy") : <span className="text-slate-500">Pick a visit date</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start" data-testid="visit-calendar">
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected || today}
              onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd"), null); setOpen(false); } }}
              disabled={[{ before: today }, { after: horizon }, { dayOfWeek: [0] }, dayFull]}
              classNames={{ day_selected: "bg-[#FF6B00] text-white hover:bg-[#E05E00] hover:text-white focus:bg-[#FF6B00] focus:text-white", day_today: "border border-[#FF6B00] text-[#E05E00] font-semibold" }}
            />
            <p className="px-3 pb-3 text-[11px] text-slate-500">Mon–Sat · next {avail?.horizon_days ?? 30} days · greyed dates are fully booked</p>
          </PopoverContent>
        </Popover>
        {date && (
          <Button variant="ghost" size="icon" className="h-11 w-11 text-slate-500" onClick={() => onChange("", null)} data-testid="visit-clear-button" aria-label="Clear visit">
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {date && (
        <div className="grid grid-cols-5 gap-2" data-testid="visit-slot-grid">
          {slots.map((s) => {
            const n = bookedFor(date, s);
            const full = n >= max;
            const active = slot === s;
            return (
              <button
                key={s}
                type="button"
                disabled={full}
                onClick={() => onChange(date, s)}
                data-testid={`visit-slot-${s.replace(":", "")}`}
                className={`rounded-lg border px-1 py-2 text-center transition-colors ${
                  active ? "border-[#FF6B00] bg-[#FFF3E8] text-[#E05E00]" : full ? "border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed" : "border-slate-200 hover:border-[#FF6B00]"
                }`}
              >
                <span className="block text-sm font-semibold">{s}</span>
                <span className="block text-[10px]">{full ? "Full" : `${max - n} left`}</span>
              </button>
            );
          })}
        </div>
      )}
      {date && !slot && <p className="text-xs text-amber-600" data-testid="visit-slot-hint">Select a time slot to confirm the visit.</p>}
    </div>
  );
};
